import { Editor } from '@tiptap/react';
import {
  REFERENCE_DOUBLE_MUSTACHE_PATTERN,
  EMPTY_PARAGRAPH_PATTERN,
} from '../regex';

/**
 * Normalizes a reference ID to ensure consistent format
 *
 * @param id Reference ID to normalize
 * @param withBraces Whether to include {{ }} around the ID
 * @returns Normalized reference ID
 */
export const normalizeReferenceId = (
  id: string,
  withBraces = false
): string => {
  if (!id) return '';

  // First, clean the ID by removing any braces
  const cleanId = id.replace(/^\{\{|\}\}$/g, '');

  // Check if it has resource prefix
  const hasResourcePrefix = cleanId.match(/^(inputs|steps)\./);

  // If no resource prefix, add default one
  const finalId = hasResourcePrefix ? cleanId : `inputs.${cleanId}`;

  // Return with or without braces as requested
  return withBraces ? `{{${finalId}}}` : finalId;
};

/**
 * Recursively converts reference nodes to template format in JSON content
 *
 * @param jsonContent Editor JSON content
 * @returns Transformed content with reference nodes converted to text nodes
 */
export const convertReferencesToTemplateFormat = (jsonContent: any): any => {
  if (!jsonContent || typeof jsonContent !== 'object') {
    return jsonContent;
  }

  // If this is a reference node, convert it to a text node with template format
  if (
    jsonContent.type === 'reference' &&
    jsonContent.attrs &&
    jsonContent.attrs.id
  ) {
    // Convert the reference node to a text node with the template format
    return {
      type: 'text',
      text: `{{${jsonContent.attrs.id}}}`,
    };
  }

  // Process arrays (like content arrays)
  if (Array.isArray(jsonContent)) {
    return jsonContent.map((item) => convertReferencesToTemplateFormat(item));
  }

  // Process object properties recursively
  const result = { ...jsonContent };
  for (const key in result) {
    if (typeof result[key] === 'object') {
      result[key] = convertReferencesToTemplateFormat(result[key]);
    }
  }

  return result;
};

/**
 * Processes all reference templates in editor content and converts them to reference nodes
 *
 * @param editor TipTap editor instance
 */
export const processAllReferences = (editor: Editor | null): void => {
  if (!editor) {
    return;
  }

  try {
    // Avoid processing in potentially unsafe states
    if (!editor.isEditable) {
      return;
    }

    // Get current content
    const currentContent = editor.getHTML();

    // Check if there's any content to process
    if (!currentContent || currentContent === '<p></p>') {
      return;
    }

    // Apply transformation using regex
    let matchCount = 0;

    // Reset lastIndex to start search from beginning
    REFERENCE_DOUBLE_MUSTACHE_PATTERN.lastIndex = 0;
    while (REFERENCE_DOUBLE_MUSTACHE_PATTERN.exec(currentContent) !== null) {
      matchCount++;
    }

    if (matchCount === 0) {
      return;
    }

    // Reset the regex for the replacement operation
    REFERENCE_DOUBLE_MUSTACHE_PATTERN.lastIndex = 0;

    // Replace {{expression}} patterns with reference spans
    // The new pattern captures everything inside braces as a single group
    const transformedContent = currentContent.replace(
      REFERENCE_DOUBLE_MUSTACHE_PATTERN,
      (match, content) => {
        // Use the entire expression between {{ and }} as the ID
        return `<span data-reference data-id="${content}"></span>`;
      }
    );

    // Only update if changes were made
    if (transformedContent !== currentContent) {
      // Use a safer approach to setting content
      requestAnimationFrame(() => {
        try {
          if (editor.isEditable) {
            editor.commands.setContent(transformedContent, false, {
              preserveWhitespace: 'full',
            });
          }
        } catch (error) {
          // Error during reference transformation, fail silently
        }
      });
    }
  } catch (error) {
    // Error during reference transformation, fail silently
  }
};

/**
 * Process HTML to handle empty paragraphs better for preview mode
 *
 * @param html HTML string to process
 * @returns Processed HTML with empty paragraphs handled
 */
export const processHtmlForPreview = (html: string): string => {
  if (!html) return '';

  // Replace empty paragraphs with a non-breaking space paragraph
  // This ensures they take up vertical space when rendered
  return html.replace(
    EMPTY_PARAGRAPH_PATTERN,
    '<p class="empty-paragraph">&nbsp;</p>'
  );
};
